import { useTranslation } from "next-i18next";
import { node } from "prop-types";
import { createContext, useEffect } from "react";

import { setZodLocale } from "~lib/validation/i18n";

/** @type {React.Context<import("~types/types").AppLocale>} */
const LocaleContext = createContext(null);

export const LocaleProvider = ({ children }) => {
  const {
    i18n: { language },
  } = useTranslation();

  useEffect(() => {
    if (!language) {
      return;
    }
    // set new zod error map for the selected language
    // we know that language is a valid zod locale
    // (one of our own defined locales in config/locale.js)
    // @ts-ignore
    setZodLocale(language);
  }, [language]);

  return (
    <LocaleContext.Provider value={language}>{children}</LocaleContext.Provider>
  );
};

/**
 * To overwrite the default zod error messages per page
 *
 * @param {(locale: import("~types/types").AppLocale) => Promise<Record<string, string>>} asyncResolver - translations object make sure this is a global object or memoized
 */
export function useZodCustomTranslationMapResolver(asyncResolver) {
  const { i18n } = useTranslation();
  const { language } = i18n;

  useEffect(() => {
    if (!language) {
      return;
    }

    asyncResolver(language).then((customMap) => {
      // set new zod error map for the selected language
      // we know that language is a valid zod locale
      // (one of our own defined locales in config/locale.js)
      // @ts-ignore
      setZodLocale(language, customMap);
    });
  }, [language, asyncResolver]);
}

LocaleProvider.propTypes = {
  children: node.isRequired,
};
